// Helper function to calculate the center of a polygon
export function getPolygonCenter(geometry) {
  if (geometry.type !== 'Polygon' || !geometry.coordinates || !geometry.coordinates[0]) {
    return null;
  }
  
  const coords = geometry.coordinates[0]; // Use outer ring
  let sumLng = 0;
  let sumLat = 0;
  
  for (const coord of coords) {
    sumLng += coord[0];
    sumLat += coord[1];
  }
  
  return [sumLng / coords.length, sumLat / coords.length];
}

// Helper function to format distance consistently
export function formatDistance(length) {
  if (length < 0.01) {
    // Very short distances in feet
    return `${(length * 5280).toFixed(1)} feet`;
  } else if (length < 1) {
    // Short distances in yards
    return `${(length * 1760).toFixed(2)} yards`;
  } else {
    // Longer distances in miles
    return `${length.toFixed(2)} miles`;
  }
}

// Helper function to calculate distance between two points using Haversine formula
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

// Helper function to calculate total distance of a linestring
export function calculateLinestringDistance(coordinates) {
  if (coordinates.length < 2) return 0;
  
  let totalDistance = 0;
  for (let i = 1; i < coordinates.length; i++) {
    const [lon1, lat1] = coordinates[i - 1];
    const [lon2, lat2] = coordinates[i];
    totalDistance += calculateDistance(lat1, lon1, lat2, lon2);
  }
  return totalDistance;
}

/**
 * Centroid of a Polygon or MultiPolygon (average of outer-ring vertices, excluding closing point).
 * GeoJSON coordinates are [lng, lat]. Returns { lat, lng } or null.
 */
export function getCentroidFromPolygonOrMultiPolygon(geojson) {
  if (!geojson?.coordinates?.length) return null;
  let lngSum = 0;
  let latSum = 0;
  let count = 0;

  function addRing(ring) {
    if (!ring?.length) return;
    for (let i = 0; i < ring.length - 1; i++) {
      lngSum += Number(ring[i][0]);
      latSum += Number(ring[i][1]);
      count += 1;
    }
  }

  if (geojson.type === 'Polygon') {
    if (geojson.coordinates[0]) addRing(geojson.coordinates[0]);
  } else if (geojson.type === 'MultiPolygon') {
    geojson.coordinates.forEach((poly) => poly[0] && addRing(poly[0]));
  }

  if (count === 0) return null;
  return { lat: latSum / count, lng: lngSum / count };
}

/**
 * Centroid of all hunt boundaries combined (average of each boundary's centroid).
 */
export function getCentroidFromBoundaries(boundaries) {
  if (!Array.isArray(boundaries) || boundaries.length === 0) return null;
  const tracts = boundaries.filter((b) => b && b.is_boundary !== false);
  if (tracts.length === 0) return null;
  const centroids = [];
  tracts.forEach((b) => {
    const c = getCentroidFromPolygonOrMultiPolygon(b?.geojson);
    if (c) centroids.push(c);
  });
  if (centroids.length === 0) return null;
  const lat = centroids.reduce((s, c) => s + c.lat, 0) / centroids.length;
  const lng = centroids.reduce((s, c) => s + c.lng, 0) / centroids.length;
  return { lat, lng };
}

// Helper function to create a circle around a point
export function createCircle(centerLng, centerLat, radiusYards) {
  if (radiusYards <= 0) return null;
  
  // Convert yards to miles
  const radiusMiles = radiusYards / 1760;
  
  // Convert miles to degrees (approximate)
  // 1 degree of latitude ≈ 69 miles
  // 1 degree of longitude ≈ 69 * cos(latitude) miles
  const latRadius = radiusMiles / 69;
  const lngRadius = radiusMiles / (69 * Math.cos(centerLat * Math.PI / 180));
  
  // Create circle with 32 points
  const points = [];
  for (let i = 0; i < 32; i++) {
    const angle = (i / 32) * 2 * Math.PI;
    const lng = centerLng + lngRadius * Math.cos(angle);
    const lat = centerLat + latRadius * Math.sin(angle);
    points.push([lng, lat]);
  }
  
  // Close the circle
  points.push(points[0]);
  
  return {
    type: 'Polygon',
    coordinates: [points]
  };
}
